import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useLayoutEffect, useState } from 'react';
import {
  fieldWorks as api, workOrders as ordersApi, offers as offersApi,
  studies as studiesApi, contractPicker,
  type FieldWork, type FieldWorkInput, type FieldWorkMeta,
  type FieldWorkKindCode, type FieldWorkStatusCode,
  type WorkOrder, type ContractPickerItem,
} from '@/api/endpoints';
import type { Client, StudySummary, Offer } from '@/types';
import { hasPermission } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from './Icon';
import { SearchableSelect } from './SearchableSelect';
import { CoordsInput } from './CoordsInput';

interface Props {
  open: boolean;
  initial?: FieldWork | null;
  clients: Client[];
  meta: FieldWorkMeta | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Wiersz terminu w formularzu. `rawText` istnieje tylko dla wpisów z importu bez rozpoznanej daty. */
interface DateRow {
  dateFrom: string;
  dateTo: string;
  rawText: string;
  note: string;
}

/**
 * Opcja dla AKTUALNIE powiązanego elementu, którego nie ma w pobranej liście kandydatów.
 *
 * Listy są ograniczone (100 najnowszych umów) albo filtrowane widocznością (opracowania), więc
 * istniejące powiązanie potrafi z nich wypaść. `SearchableSelect` dla wartości spoza opcji
 * pokazuje placeholder — czyli pole wygląda na puste, mimo że powiązanie istnieje, i oferuje
 * krzyżyk „wyczyść". Ten helper dokleja brakującą opcję, żeby użytkownik widział, co kasuje.
 */
function biezacaOpcja(id: number | null | undefined, dostepne: number[], etykieta: string) {
  if (!id || dostepne.includes(id)) return null;
  return <option value={String(id)}>{etykieta} (poza listą)</option>;
}

const EMPTY: FieldWorkInput = {
  clientId: null, title: '', description: '', kind: 'REGENERACJA', status: 'ZLECONE',
  wellLabel: '', locationText: '', parcel: '',
  latitude: null, longitude: null,
  contractDeadline: '', deadlineNote: '', plannedDate: '',
  netAmount: null, invoicedDate: '', notes: '',
  // Powiązania MUSZĄ być w stanie formularza i MUSZĄ wracać na serwer. Wcześniej ich tu nie
  // było, a backend nadpisuje je tym, co dostanie — więc każda edycja pracy po cichu zrywała
  // wszystkie powiązania. Puste pole = świadome odpięcie, brak pola = utrata danych.
  intakeId: null, studyId: null, contractId: null, managerId: null,
  orderId: null, offerId: null,
};

/**
 * Dodanie / edycja pracy terenowej.
 *
 * Terminy wykonania są listą dynamiczną — w arkuszu były sztywnymi kolumnami „Termin
 * wykonania 1..10" i przy szóstym wyjeździe zaczynało brakować miejsca. Przycisk „Dodaj termin"
 * stoi POD listą, zgodnie z konwencją pozostałych list w portalu.
 */
export function PracaFormModal({ open, initial, clients, meta, onClose, onSaved }: Props) {
  const { user } = useAuth();
  const canSeePricing = hasPermission(user, 'PRICING_VIEW');

  const canSeeOffers = hasPermission(user, 'OFFER_VIEW');
  const canSeeContracts = hasPermission(user, 'CONTRACT_VIEW');
  const canEditOrders = hasPermission(user, 'FIELDWORK_ORDER_EDIT');

  const [data, setData] = useState<FieldWorkInput>(EMPTY);
  const [dates, setDates] = useState<DateRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Kandydaci do powiązań. Zlecenia, oferty i opracowania są ZAWSZE zawężone do wybranego
  // klienta — pełna lista ofert ciągnie treść każdej z nich, a i tak nikt nie wiąże pracy
  // ze zleceniem innego kontrahenta. Umowy idą osobnym, chudym pickerem: filtr po kliencie
  // w /api/contracts prowadzi przez study→intake→client, więc dla kontrahentów z importu
  // (bez opracowań i ujęć) zwróciłby pustą listę.
  const [orderOpts, setOrderOpts] = useState<WorkOrder[]>([]);
  const [offerOpts, setOfferOpts] = useState<Offer[]>([]);
  const [studyOpts, setStudyOpts] = useState<StudySummary[]>([]);
  const [contractOpts, setContractOpts] = useState<ContractPickerItem[]>([]);

  useLayoutEffect(() => {
    if (!open) return;
    resetDirty();
    setErr(null);
    setBusy(false);
    if (initial) {
      setData({
        clientId: initial.clientId,
        title: initial.title,
        description: initial.description ?? '',
        kind: initial.kind,
        status: initial.status,
        wellLabel: initial.wellLabel ?? '',
        locationText: initial.locationText ?? '',
        parcel: initial.parcel ?? '',
        latitude: initial.latitude,
        longitude: initial.longitude,
        contractDeadline: initial.contractDeadline ?? '',
        deadlineNote: initial.deadlineNote ?? '',
        plannedDate: initial.plannedDate ?? '',
        netAmount: initial.netAmount,
        invoicedDate: initial.invoicedDate ?? '',
        notes: initial.notes ?? '',
        intakeId: initial.intakeId,
        studyId: initial.studyId,
        contractId: initial.contractId,
        managerId: initial.managerId,
        orderId: initial.orderId,
        offerId: initial.offerId,
      });
      setDates(initial.dates.map(d => ({
        dateFrom: d.dateFrom ?? '',
        dateTo: d.dateTo ?? '',
        rawText: d.rawText ?? '',
        note: d.note ?? '',
      })));
    } else {
      setData(EMPTY);
      setDates([]);
    }
  }, [open, initial]);

  // Kandydaci przeładowują się przy zmianie klienta — inaczej po przełączeniu kontrahenta
  // w rozwijanych listach zostałyby zlecenia poprzedniego. Spóźniona odpowiedź dla klienta,
  // który już nie jest wybrany, nie może nadpisać list bieżącego (szybkie przełączanie albo
  // ponowne otwarcie modalu — pierwszy render ma jeszcze klienta poprzedniej pracy).
  useEffect(() => {
    if (!open || !data.clientId) {
      setOrderOpts([]); setOfferOpts([]); setStudyOpts([]);
      return;
    }
    const cid = data.clientId;
    let alive = true;
    const load = <T,>(req: Promise<T[]>, set: (list: T[]) => void) =>
      req.then(list => { if (alive) set(list); }).catch(() => { if (alive) set([]); });
    if (canEditOrders) load(ordersApi.byClient(cid), setOrderOpts);
    if (canSeeOffers) load(offersApi.list(undefined, cid), setOfferOpts);
    load(studiesApi.list(undefined, undefined, cid), setStudyOpts);
    return () => { alive = false; };
  }, [open, data.clientId, canEditOrders, canSeeOffers]);

  useEffect(() => {
    if (!open || !canSeeContracts) return;
    contractPicker.search(undefined, 100).then(setContractOpts).catch(() => setContractOpts([]));
  }, [open, canSeeContracts]);

  const { requestClose, confirmation, dialogProps, resetDirty } = useUnsavedChanges({
    value: { data, dates }, onClose, open, busy,
  });

  if (!open) return null;

  const set = <K extends keyof FieldWorkInput>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setData(d => ({ ...d, [k]: e.target.value }));

  const setDateRow = (i: number, patch: Partial<DateRow>) =>
    setDates(rows => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.clientId) { setErr('Wybierz klienta'); return; }
    if (!data.title?.trim()) { setErr('Opis prac jest wymagany'); return; }
    if ((data.latitude == null) !== (data.longitude == null)) {
      setErr('Podaj obie współrzędne studni albo żadnej');
      return;
    }

    const payload: FieldWorkInput = {
      ...data,
      title: data.title.trim(),
      description: data.description?.trim() || null,
      wellLabel: data.wellLabel?.trim() || null,
      locationText: data.locationText?.trim() || null,
      parcel: data.parcel?.trim() || null,
      contractDeadline: data.contractDeadline || null,
      deadlineNote: data.deadlineNote?.trim() || null,
      plannedDate: data.plannedDate || null,
      invoicedDate: data.invoicedDate || null,
      notes: data.notes?.trim() || null,
      // Kwota bez PRICING_VIEW w ogóle nie jedzie — backend i tak jej nie ruszy, ale nie ma
      // powodu wysyłać nulla, który wygląda jak intencja wyzerowania.
      netAmount: canSeePricing ? (data.netAmount ?? null) : undefined,
      dates: dates
        .filter(r => r.dateFrom || r.dateTo || r.rawText)
        .map(r => ({
          // Wiersz z wypełnionym tylko „Do" traktujemy jako termin jednodniowy w tej dacie,
          // zamiast go po cichu wyrzucać: użytkownik coś wpisał i ma prawo to zobaczyć.
          dateFrom: r.dateFrom || r.dateTo || null,
          dateTo: r.dateFrom ? (r.dateTo || null) : null,
          rawText: (r.dateFrom || r.dateTo) ? null : (r.rawText || null),
          note: r.note || null,
        })),
    };

    setBusy(true);
    const op = initial ? api.update(initial.id, payload) : api.create(payload);
    op.then(() => { onSaved(); onClose(); })
      .catch(e2 => setErr(e2?.message ?? String(e2)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[820px] max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-100 sticky top-0 bg-white z-10">
          <h2 className="text-[16px] font-semibold text-ink-900">
            {initial ? `Edytuj pracę #${initial.seqNo}` : 'Nowa praca terenowa'}
          </h2>
          <button type="button" onClick={requestClose} className="btn btn-ghost btn-icon text-ink-400 hover:text-ink-700">
            <Icon name="x"/>
          </button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          <label className="field col-span-2">
            <span className="field-label">Klient *</span>
            {/* Zmiana kontrahenta unieważnia powiązania zawężone do klienta. Bez tego pole pokazywało
                „— brak —" (bo opcji już nie ma na liście), a w payloadzie leciało STARE id — praca
                klienta B lądowała na zleceniu klienta A. Serwer ma na to własną bramkę, ale użytkownik
                ma zobaczyć skutek od razu, a nie dostać 400 przy zapisie.
                Czyścimy TU, na wybór użytkownika, a nie efektem pilnującym clientId: modal zostaje
                zamontowany między edycjami, więc po otwarciu kolejnej pracy taki efekt widział
                „klient poprzedniej pracy → klient tej" i po cichu kasował jej powiązania. */}
            <SearchableSelect value={data.clientId ? String(data.clientId) : ''}
                              onChange={v => setData(d => {
                                const clientId = v ? Number(v) : null;
                                return clientId === d.clientId
                                  ? d : { ...d, clientId, orderId: null, offerId: null, studyId: null };
                              })}>
              <option value="">— wybierz —</option>
              {clients.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </SearchableSelect>
          </label>

          <label className="field col-span-2">
            <span className="field-label">Opis prac *</span>
            <input value={data.title ?? ''} onChange={set('title')} className="input" autoFocus
                   placeholder="np. Regeneracja studni głębinowej nr 3/02 w miejscowości Witankowo"/>
          </label>

          <label className="field">
            <span className="field-label">Rodzaj</span>
            <SearchableSelect value={data.kind ?? 'REGENERACJA'}
                              onChange={v => setData(d => ({ ...d, kind: v as FieldWorkKindCode }))}>
              {(meta?.kinds ?? []).map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </SearchableSelect>
          </label>
          <label className="field">
            <span className="field-label">Status</span>
            <SearchableSelect value={data.status ?? 'ZLECONE'}
                              onChange={v => setData(d => ({ ...d, status: v as FieldWorkStatusCode }))}>
              {(meta?.statuses ?? []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </SearchableSelect>
          </label>

          <label className="field">
            <span className="field-label">Studnia / otwór</span>
            <input value={data.wellLabel ?? ''} onChange={set('wellLabel')} className="input"
                   placeholder="np. SW6/65, nr 1a"/>
          </label>
          <label className="field">
            <span className="field-label">Miejscowość / ujęcie</span>
            <input value={data.locationText ?? ''} onChange={set('locationText')} className="input"
                   placeholder="np. Witankowo, ujęcie Świerczewo"/>
          </label>
          <label className="field col-span-2">
            <span className="field-label">Działka</span>
            <input value={data.parcel ?? ''} onChange={set('parcel')} className="input font-mono"
                   placeholder="np. 1343/101 obręb 0002"/>
          </label>

          {/* Współrzędne SAMEJ studni — puste znaczy „pokaż w punkcie klienta". Bez tego mapa
              kłamałaby przy kliencie z kilkoma ujęciami rozrzuconymi po gminie. */}
          <div className="col-span-2 border-t border-ink-100 pt-3 mt-1 text-[12px] text-ink-500">
            Współrzędne studni. Zostaw puste — na mapie praca stanie w punkcie klienta.
          </div>
          <CoordsInput
            latitude={data.latitude}
            longitude={data.longitude}
            onChange={(lat, lng) => setData(d => ({ ...d, latitude: lat, longitude: lng }))}
            layout="stacked"
            label="Współrzędne studni"
          />

          {/* ── Powiązania ────────────────────────────────────────────────────────
              Cztery niezależne pola, żadne niewymagane: zlecenie bywa bez oferty, oferta bez
              umowy, a praca terenowa zwykle bez opracowania. Każde pole pokazujemy tylko temu,
              kto ma do niego uprawnienie — kto go nie widzi, ten go też nie skasuje (backend
              pomija pola bez uprawnienia zamiast je zerować). */}
          <div className="col-span-2 border-t border-ink-100 pt-3 mt-1">
            <div className="field-label mb-1">Powiązania</div>
            <div className="text-[12px] text-ink-400 mb-2">
              Wszystkie opcjonalne. Zlecenie telefoniczne nie musi mieć oferty ani umowy.
            </div>
          </div>

          {canEditOrders && (
            <label className="field">
              <span className="field-label">Zlecenie</span>
              <SearchableSelect value={data.orderId ? String(data.orderId) : ''} clearable
                                disabled={!data.clientId}
                                onChange={v => setData(d => ({ ...d, orderId: v ? Number(v) : null }))}
                                placeholder={data.clientId ? '— brak —' : '— najpierw klient —'}>
                {biezacaOpcja(data.orderId, orderOpts.map(o => o.id),
                  initial?.orderNumber ?? `zlecenie #${data.orderId}`)}
                {orderOpts.map(o => (
                  <option key={o.id} value={String(o.id)}>
                    {o.number} — {o.title}{o.cancelled ? ' (anulowane)' : ''}
                  </option>
                ))}
              </SearchableSelect>
            </label>
          )}

          {canSeeOffers && (
            <label className="field">
              <span className="field-label">Oferta</span>
              <SearchableSelect value={data.offerId ? String(data.offerId) : ''} clearable
                                disabled={!data.clientId}
                                onChange={v => setData(d => ({ ...d, offerId: v ? Number(v) : null }))}
                                placeholder={data.clientId ? '— brak —' : '— najpierw klient —'}>
                {biezacaOpcja(data.offerId, offerOpts.map(o => o.id),
                  initial?.offerNumber ?? `oferta #${data.offerId}`)}
                {offerOpts.map(o => (
                  <option key={o.id} value={String(o.id)}>{o.number} — {o.title}</option>
                ))}
              </SearchableSelect>
            </label>
          )}

          {canSeeContracts && (
            <label className="field">
              <span className="field-label">Umowa</span>
              <SearchableSelect value={data.contractId ? String(data.contractId) : ''} clearable
                                onChange={v => setData(d => ({ ...d, contractId: v ? Number(v) : null }))}
                                placeholder="— brak —">
                {/* Picker zwraca 100 najnowszych umów. Gdy praca wskazuje starszą, MUSI ona i tak
                    być opcją — inaczej pole pokazuje „— brak —" obok krzyżyka „wyczyść" i
                    użytkownik kasuje powiązanie, którego nigdy nie zobaczył. */}
                {biezacaOpcja(data.contractId, contractOpts.map(c => c.id),
                  initial?.contractNumber ?? `umowa #${data.contractId}`)}
                {contractOpts.map(c => (
                  <option key={c.id} value={String(c.id)}>
                    {c.number}{c.signedDate ? ` — podpisana ${c.signedDate}` : ''}
                  </option>
                ))}
              </SearchableSelect>
            </label>
          )}

          <label className="field">
            <span className="field-label">Opracowanie</span>
            <SearchableSelect value={data.studyId ? String(data.studyId) : ''} clearable
                              disabled={!data.clientId}
                              onChange={v => setData(d => ({ ...d, studyId: v ? Number(v) : null }))}
                              placeholder={data.clientId ? '— brak —' : '— najpierw klient —'}>
              {biezacaOpcja(data.studyId, studyOpts.map(o => o.id),
                initial?.studyNumber ?? `opracowanie #${data.studyId}`)}
              {studyOpts.map(o => (
                <option key={o.id} value={String(o.id)}>{o.number} — {o.title}</option>
              ))}
            </SearchableSelect>
          </label>

          {/* ── Terminy wykonania ─────────────────────────────────────────────── */}
          <div className="col-span-2 border-t border-ink-100 pt-3 mt-1">
            <div className="field-label mb-1">Terminy wykonania</div>
            {dates.length === 0 && (
              <div className="text-[12px] text-ink-400 mb-2">
                Brak wpisów. Dodaj dzień lub zakres dni, w których brygada była na obiekcie.
              </div>
            )}
            <div className="grid gap-2">
              {dates.map((r, i) => (
                <div key={i} className="flex items-end gap-2">
                  {r.rawText && !r.dateFrom ? (
                    // Wpis z importu, którego nie dało się rozłożyć na daty. Pokazujemy oryginał
                    // i pozwalamy go zastąpić datami — kasowanie go w tle gubiłoby dane wejściowe.
                    <>
                      <label className="field flex-1">
                        <span className="field-label">Zapis źródłowy (nierozpoznany)</span>
                        <input value={r.rawText} onChange={e => setDateRow(i, { rawText: e.target.value })}
                               className="input italic text-ink-600"/>
                      </label>
                      <label className="field w-[150px]">
                        <span className="field-label">Zastąp datą</span>
                        <input type="date" value={r.dateFrom}
                               onChange={e => setDateRow(i, { dateFrom: e.target.value })} className="input"/>
                      </label>
                    </>
                  ) : (
                    <>
                      <label className="field w-[150px]">
                        <span className="field-label">Od</span>
                        <input type="date" value={r.dateFrom}
                               onChange={e => setDateRow(i, { dateFrom: e.target.value })} className="input"/>
                      </label>
                      <label className="field w-[150px]">
                        <span className="field-label">Do (opcjonalnie)</span>
                        <input type="date" value={r.dateTo}
                               onChange={e => setDateRow(i, { dateTo: e.target.value })} className="input"/>
                      </label>
                      <label className="field flex-1">
                        <span className="field-label">Uwaga</span>
                        <input value={r.note} onChange={e => setDateRow(i, { note: e.target.value })}
                               className="input" placeholder="np. etap I — udrożnienie"/>
                      </label>
                    </>
                  )}
                  <button type="button" onClick={() => setDates(rows => rows.filter((_, idx) => idx !== i))}
                          className="btn btn-ghost btn-icon text-ink-400 hover:text-red-600 mb-1" title="Usuń termin">
                    <Icon name="x" className="w-3.5 h-3.5"/>
                  </button>
                </div>
              ))}
            </div>
            <button type="button"
                    onClick={() => setDates(rows => [...rows, { dateFrom: '', dateTo: '', rawText: '', note: '' }])}
                    className="btn btn-secondary btn-sm mt-2">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj termin
            </button>
          </div>

          {/* ── Terminy umowne i rozliczenie ──────────────────────────────────── */}
          <label className="field">
            <span className="field-label">Termin z umowy</span>
            <input type="date" value={data.contractDeadline ?? ''} onChange={set('contractDeadline')} className="input"/>
          </label>
          <label className="field">
            <span className="field-label">Termin warunkowy (gdy brak daty)</span>
            <input value={data.deadlineNote ?? ''} onChange={set('deadlineNote')} className="input"
                   placeholder="np. 60 dni od podpisania umowy"/>
          </label>
          <label className="field">
            <span className="field-label">Planowany termin rozpoczęcia</span>
            <input type="date" value={data.plannedDate ?? ''} onChange={set('plannedDate')} className="input"/>
          </label>

          {canSeePricing && (
            <label className="field">
              <span className="field-label">Kwota netto (PLN)</span>
              <input type="number" step="0.01" value={data.netAmount ?? ''}
                     onChange={e => setData(d => ({ ...d, netAmount: e.target.value === '' ? null : Number(e.target.value) }))}
                     className="input font-mono" placeholder="0.00"/>
            </label>
          )}
          {canSeePricing && (
            <label className="field col-span-2">
              <span className="field-label">Data faktury (gdy brak dokumentu w systemie)</span>
              <input type="date" value={data.invoicedDate ?? ''} onChange={set('invoicedDate')} className="input"/>
              <span className="text-[11px] text-ink-400 mt-0.5">
                Dla prac historycznych. Fakturę z systemu przypina się na karcie pracy — wtedy to pole
                nie jest potrzebne.
              </span>
            </label>
          )}

          <label className="field col-span-2">
            <span className="field-label">Uwagi</span>
            <textarea value={data.notes ?? ''} onChange={set('notes')} className="textarea" rows={2}/>
          </label>
          <label className="field col-span-2">
            <span className="field-label">Szczegółowy opis / zakres</span>
            <textarea value={data.description ?? ''} onChange={set('description')} className="textarea" rows={3}/>
          </label>

          {err && <div className="col-span-2 text-red-600 text-[12px]">Błąd: {err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={requestClose} className="btn btn-secondary">Anuluj</button>
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Zapisywanie…' : (initial ? 'Zapisz zmiany' : 'Dodaj pracę')}
          </button>
        </div>
      </form>
    </div>
  );
}
